import { isEmpty } from 'lodash-es';

import { getBidderGroups } from '~/src/dash/api/bidder-group';
import { getChannelGroupsPromise } from '~/src/dash/api/channel-group';
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  getPublisherGroup,
  HeaderBidderActiveActive,
  IHeaderBidder,
  IPublisher,
  queryHeaderBidders,
} from '~/src/dash/api/console-api';
import { getAllDeals } from '~/src/dash/api/deals';
import { queryDemandSources } from '~/src/dash/api/demand_sources';
import { getPublishersSimplifiedV1 } from '~/src/dash/api/publisher';
import { EActive } from '~/src/dash/api/types/active';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  getHeaderBidderAdaptersForPublisher,
  IHeaderBidderAdapter,
} from '~/src/dash/utils/header-bidder-adapters';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';

export const dashboardMainChartColorBank = {
  blue: '#5D66FA',
  red: '#FF4E29',
  cyan: '#ACF8F4',
  mustard: '#F3C628',
  coralPink: '#FB6376',
  mahogany: '#A75E6E',
  blueGray: '#84BCEF',
  darkPeach: '#F4B583',
  forestGreen: '#40916C',
  seaGreen: '#85FFC7',
  ochre: '#E6DFA4',
  pink: '#FF9CEF',
  purple: '#6F60DD',
  yellow: '#FFFD82',
  gray: '#8E9AAF',
  orange: '#FFA737',
  turquoise: '#00B7A9',
  lavender: '#DAC3E8',
  limeGreen: '#B2E061',
  tan: '#C6AC8F',
  sienna: '#E88160',
  palePink: '#E8C2D2',
  darkBlue: '#014F86',
  purpleBlue: '#95619C',
  darkGray: '#8B8C89',
};

const generateDemandSourceList = (demandSourceAdapters: IHeaderBidderAdapter[]) => {
  const demandSourceList = {} as Record<string, IListItem>;
  demandSourceAdapters.forEach((adapter) => {
    demandSourceList[adapter.key] = {
      ID: adapter.key,
      title: adapter.text,
      selected: false,
    };
  });
  return demandSourceList;
};

const generateFilteredDemandSourceList = (
  demandSourceAdapters: IHeaderBidderAdapter[],
  availableDemandSources: Record<string, boolean>,
) => {
  const demandSourceList = {} as Record<string, IListItem>;
  demandSourceAdapters.forEach((adapter) => {
    if (availableDemandSources[adapter.key]) {
      demandSourceList[adapter.key] = {
        ID: adapter.key,
        title: adapter.text,
        selected: false,
      };
    }
  });
  return demandSourceList;
};

export const generateDemandSourcesList = (
  session: ISessionState,
  user: IUserState,
  sspStr = '',
  showAllBidders = false,
  isDashboard = true,
): Promise<Record<string, IMenuList>> => {
  // TODO: CHECK HOW TO HANDLE DEPRECATED
  const demandSourceAdapters = [
    ...getHeaderBidderAdaptersForPublisher(
      sspStr,
      showAllBidders,
      session.publisher,
      hasRoleSuperUser(user.user),
      isDashboard,
    ),
    // ...unlistedHeaderBidderAdapters
  ];
  const list = generateDemandSourceList(demandSourceAdapters);
  return new Promise((resolve) => resolve({ 'Demand Sources': list }));
};

export async function getSmallSitesList(
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> {
  // get sites
  const [small_sites, err] = await getSmallSites(session.publisher.ID);
  if (err) {
    console.error(err);
    return { Channels: {} };
  }
  if (small_sites !== undefined) {
    const siteList: IMenuList = {};
    small_sites.forEach((site: ISmallSite) => {
      const siteName = checkUserInfoMasked(user.user) ? `demo_site_${site.ID}` : site.Name;
      siteList[site.ID] = {
        title: siteName,
        selected: false,
        ID: site.ID.toString(),
        active: site.Active === EActive.ACTIVE,
      };
    });
    return { Channels: siteList };
  }
  return { Channels: {} };
}

export const getChannelGroupsList = async (
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> => {
  const channelGroupsObject: any = await getChannelGroupsPromise({
    publisherID: session.publisher.ID,
  });
  const channelGroupsList: IMenuList = {};
  Object.values(channelGroupsObject).forEach((channelGroups: any) => {
    channelGroups.forEach((channelGroup: any) => {
      const ID = channelGroup.ID.toString();
      const channelGroupName = checkUserInfoMasked(user.user)
        ? `demo_channel_group_${ID}`
        : channelGroup.Name;
      channelGroupsList[channelGroup.ID] = {
        title: channelGroupName,
        selected: false,
        ID,
        channelGroup,
      };
    });
  });
  return { 'Channel Groups': channelGroupsList };
};

export const getAssociatedPublisherIDs = async (publisherID: string): Promise<string[]> => {
  const pubIDs: string[] = [];
  return getPublisherGroup({
    query: { publisher_id: publisherID },
  }).then((pubs: IPublisher[]) => {
    pubs.forEach((pub) => {
      pubIDs.push(pub.ID);
    });
    if (isEmpty(pubIDs)) {
      return [publisherID];
    } else return pubIDs;
  });
};

export const getPublishersList = async (
  session: ISessionState,
  _user: IUserState,
): Promise<Record<string, IMenuList>> => {
  return getPublisherGroup({
    query: {
      publisher_id: session.publisher.ID,
    },
  }).then((pubs: IPublisher[]) => {
    const pubsList: IMenuList = {};
    pubs.forEach((pub) => {
      pubsList[pub.ID] = {
        title: pub.Name,
        selected: false,
        ID: pub.ID.toString(),
      };
    });
    return { Publishers: pubsList };
  });
};

export const getPublicaPublishersList = async (
  _session: ISessionState,
  _user: IUserState,
): Promise<Record<string, IMenuList>> => {
  return getPublishersSimplifiedV1().then((resp: any) => {
    const pubs = resp[0];
    const pubsList: IMenuList = {};
    pubs.forEach((publisher: IPublisher) => {
      pubsList[publisher.ID] = {
        title: publisher.Name,
        selected: false,
        ID: publisher.ID.toString(),
      };
    });
    return { 'Publica Publishers': pubsList };
  });
};

export const getBiddersList = (
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> => {
  return queryHeaderBidders({
    query: {
      publisher_id: session.publisher.ID,
    },
  }).then((bidders: IHeaderBidder[]) => {
    const bidderList: IMenuList = {};
    bidders.forEach((bidder) => {
      const bidderName = checkUserInfoMasked(user.user) ? `demo_bidder_` + bidder.ID : bidder.Name;
      bidderList[bidder.ID] = {
        title: bidderName,
        selected: false,
        ID: bidder.ID,
        active: bidder.Active === HeaderBidderActiveActive,
      };
    });
    return { Bidders: bidderList };
  });
};

export const getBidderGroupsList = async (
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> => {
  const bidderGroupsObject: any = await getBidderGroups({
    query: {
      publisher_id: session.publisher.ID,
    },
  });
  const bidderGroupsList: IMenuList = {};
  Object.values(bidderGroupsObject).forEach((bidderGroups: any) => {
    bidderGroups.forEach((bidderGroup: any) => {
      const ID = bidderGroup.ID;
      const bidderName = checkUserInfoMasked(user.user)
        ? `demo_bidder_group_${ID}`
        : bidderGroup.Name;
      bidderGroupsList[bidderGroup.ID] = {
        title: bidderName,
        selected: false,
        ID,
        bidderGroup,
      };
    });
  });
  return { 'Bidder Groups': bidderGroupsList };
};

export const getDemandSources = (
  session: ISessionState,
  user: IUserState,
  siteIDs: string = '',
): Promise<Record<string, IMenuList>> => {
  return queryDemandSources({
    query: {
      publisher_id: session.publisher.ID,
      site_ids: siteIDs,
    },
  })
    .then((demandSources: Record<string, boolean>) => {
      const demandSourceAdapters = [
        ...getHeaderBidderAdaptersForPublisher(
          '',
          false,
          session.publisher,
          hasRoleSuperUser(user.user),
          true,
        ),
      ];
      const filteredDemandSources = generateFilteredDemandSourceList(
        demandSourceAdapters,
        demandSources,
      );
      return { 'Filtered Demand Sources': filteredDemandSources };
    })
    .catch(() => {
      return { 'Filtered Demand Sources': {} };
    });
};

export const getDeals = (
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> => {
  return getAllDeals(session.publisher.ID)
    .then((responses: any) => {
      const dealList = {} as IMenuList;
      responses.forEach((resp: any) => {
        if (resp.status === 'fulfilled') {
          const deals = resp.value;
          for (let i = 0; i < deals.length; i++) {
            const deal = deals[i];
            const title = checkUserInfoMasked(user.user) ? `demo_deal_${deal.ID}` : deal.Name;
            dealList[deal.ID] = {
              ID: deal.ID.toString(),
              selected: false,
              title,
            } as IListItem;
          }
        }
      });
      return { 'Outbound Deals': dealList };
    })
    .catch(() => {
      return { 'Outbound Deals': {} };
    });
};
